import { apiClient } from './apiClient';
import { httpErrorDetailMessage } from '../utils/httpErrorDetail';
import {
  formatPersistModeNotPatchMessage,
  normalizeControlBridgePersistMode,
  normalizeControlBridgeSotPlane,
  type ControlBridgePersistMode,
  type ControlBridgeSotPlane,
} from './controlBridgePersistMode';

/** Allowlist mirrors Backend ``_SPAWN_ALLOWED_TYPES``. */
export type SpawnArtifactTypeRef = 'pt-quote' | 'pt-bsc-issue' | 'pt-po-issue' | 'pt-sp-issue';

export interface SpawnArtifactWriteBase {
  artifactItemCount?: number;
  itemIdSetHash?: string;
}

export interface SpawnArtifactItemRequest {
  artifactTypeRef: SpawnArtifactTypeRef | string;
  name: string;
  description?: string;
  companyArtifactRef: string;
  filterScope?: string;
  /** Same shape as Exo ``propertyPatches`` (key / value / optional global_property_ref). */
  properties?: Array<{ key: string; value: string; global_property_ref?: string }>;
  writeBase?: SpawnArtifactWriteBase;
}

export interface SpawnArtifactItemResponse {
  ok: boolean;
  itemId: string;
  artifactTypeRef?: string;
  currentState?: string;
  workspaceSlug?: string;
  persistMode?: ControlBridgePersistMode;
  sotPlane?: ControlBridgeSotPlane;
  source?: 'db' | 'workspace';
  companyArtifactRef?: string;
  writeBase?: SpawnArtifactWriteBase;
  method?: string;
  note?: string;
}

export class ControlBridgeSpawnArtifactError extends Error {
  constructor(
    message: string,
    readonly code?: string,
    readonly httpStatus?: number
  ) {
    super(message);
    this.name = 'ControlBridgeSpawnArtifactError';
  }
}

export function assertSpawnArtifactPersistModePatch(
  response: SpawnArtifactItemResponse
): void {
  if (response.persistMode === 'patch') return;
  throw new ControlBridgeSpawnArtifactError(
    formatPersistModeNotPatchMessage(
      'Spawn-artifact-item',
      response.persistMode,
      response.sotPlane
    ),
    'PERSIST_MODE_NOT_PATCH'
  );
}

function asRecord(value: unknown): Record<string, unknown> | null {
  return value && typeof value === 'object' && !Array.isArray(value)
    ? (value as Record<string, unknown>)
    : null;
}

function normalizeWriteBase(raw: unknown): SpawnArtifactWriteBase | undefined {
  const obj = asRecord(raw);
  if (!obj) return undefined;
  const count = obj.artifactItemCount ?? obj.artifact_item_count;
  const hash = obj.itemIdSetHash ?? obj.item_id_set_hash;
  const out: SpawnArtifactWriteBase = {};
  if (typeof count === 'number') out.artifactItemCount = count;
  if (typeof hash === 'string') out.itemIdSetHash = hash;
  return Object.keys(out).length ? out : undefined;
}

export function normalizeSpawnArtifactResponse(
  data: unknown,
  fallbackItemId: string
): SpawnArtifactItemResponse {
  const row = asRecord(data) ?? {};
  const sourceRaw = row.source;
  return {
    ok: row.ok !== false,
    itemId:
      (typeof row.itemId === 'string' && row.itemId) ||
      (typeof row.item_id === 'string' && row.item_id) ||
      fallbackItemId,
    artifactTypeRef:
      (typeof row.artifactTypeRef === 'string' && row.artifactTypeRef) ||
      (typeof row.artifact_type_ref === 'string' ? row.artifact_type_ref : undefined),
    currentState:
      (typeof row.currentState === 'string' && row.currentState) ||
      (typeof row.current_state === 'string' ? row.current_state : undefined),
    workspaceSlug:
      (typeof row.workspaceSlug === 'string' && row.workspaceSlug) ||
      (typeof row.workspace_slug === 'string' ? row.workspace_slug : undefined),
    persistMode: normalizeControlBridgePersistMode(row.persistMode ?? row.persist_mode),
    sotPlane: normalizeControlBridgeSotPlane(row.sotPlane ?? row.sot_plane),
    source: sourceRaw === 'db' || sourceRaw === 'workspace' ? sourceRaw : undefined,
    companyArtifactRef:
      (typeof row.companyArtifactRef === 'string' && row.companyArtifactRef) ||
      (typeof row.company_artifact_ref === 'string'
        ? row.company_artifact_ref
        : undefined),
    writeBase: normalizeWriteBase(row.writeBase ?? row.write_base ?? row.write_base_model),
    method: typeof row.method === 'string' ? row.method : undefined,
    note: typeof row.note === 'string' ? row.note : undefined,
  };
}

/**
 * POST /kryspace/workspace/{slug}/spawn-artifact-item
 * Same camelCase body + persistMode=patch fail-closed contract as report-issue / Exo.
 */
export async function spawnControlBridgeArtifactItem(
  workspaceSlug: string,
  request: SpawnArtifactItemRequest
): Promise<SpawnArtifactItemResponse> {
  const slug = workspaceSlug.trim();
  const name = request.name.trim();
  const companyArtifactRef = request.companyArtifactRef.trim();
  const artifactTypeRef = request.artifactTypeRef.trim();
  if (!slug || !name || !companyArtifactRef || !artifactTypeRef) {
    throw new ControlBridgeSpawnArtifactError(
      'Workspace slug, artifactTypeRef, name, and companyArtifactRef are required',
      'NOT_CONFIGURED'
    );
  }

  try {
    const { data } = await apiClient.post<unknown>(
      `/kryspace/workspace/${encodeURIComponent(slug)}/spawn-artifact-item`,
      {
        artifactTypeRef,
        name,
        description: request.description?.trim() || undefined,
        companyArtifactRef,
        filterScope: request.filterScope?.trim() || undefined,
        properties: request.properties,
        writeBase: request.writeBase,
      }
    );
    const normalized = normalizeSpawnArtifactResponse(data, '');
    assertSpawnArtifactPersistModePatch(normalized);
    return normalized;
  } catch (err: unknown) {
    if (err instanceof ControlBridgeSpawnArtifactError) {
      throw err;
    }
    const axiosErr = err as {
      response?: { status?: number; data?: { detail?: unknown } };
      message?: string;
    };
    const detail = axiosErr.response?.data?.detail;
    const status = axiosErr.response?.status;
    const message =
      status === 409
        ? 'Workspace changed while saving — refresh and try again'
        : httpErrorDetailMessage(detail, axiosErr.message || 'Failed to spawn artifact item');
    throw new ControlBridgeSpawnArtifactError(
      message,
      status === 409 ? 'WRITE_BASE_STALE' : undefined,
      status
    );
  }
}
